/**
 * RungBadge — the Automation Ladder rung, rendered as a tinted pill with its
 * number + label. A dedicated badge (NOT StatusBadge): the rung is a 1..5 scale
 * with its own intent mapping (UI-SPEC §0 status taxonomy).
 *
 *   ① agent · ② small-model · ③ cache · ④ script · ⑤ eliminated
 *
 * The optimizer drives steps DOWN the ladder (cheaper); self-healing pushes
 * them back UP on failure. See docs/optimization/PRD.md §4.1.
 */

import * as React from "react";
import { IntentBadge } from "../status-badge";
import type { Intent } from "../../tokens";

export type Rung = 1 | 2 | 3 | 4 | 5;

const RUNG_LABEL: Record<Rung, string> = {
  1: "agent",
  2: "small-model",
  3: "cache",
  4: "script",
  5: "eliminated",
};

// UI-SPEC §0: 1→info, 2→neutral, 3→muted, 4→success, 5→muted. "muted" is not
// one of the five intents, so it resolves to neutral.
const RUNG_INTENT: Record<Rung, Intent> = {
  1: "info",
  2: "neutral",
  3: "neutral",
  4: "success",
  5: "neutral",
};

const CIRCLED = ["①", "②", "③", "④", "⑤"] as const;

function clampRung(n: number): Rung {
  if (n <= 1) return 1;
  if (n >= 5) return 5;
  return n as Rung;
}

export function RungBadge({
  rung,
  showLabel = true,
  className,
}: {
  rung: number;
  showLabel?: boolean;
  className?: string;
}) {
  const r = clampRung(rung);
  return (
    <IntentBadge intent={RUNG_INTENT[r]} className={className}>
      <span aria-hidden>{CIRCLED[r - 1]}</span>
      {showLabel ? <span>{RUNG_LABEL[r]}</span> : null}
      <span className="sr-only">rung {r}</span>
    </IntentBadge>
  );
}
